import type { MarketMonitorPreview, MarketMonitorWrite } from "./index";
import { AMAZON_REVIEWS_CAPABILITY } from './amazonReviews';
import { amazonStoreTarget } from './amazonStore';

const capability = "amazon.products.collect_by_url";
const object = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !Array.isArray(value);
const array = (value: unknown) =>
  Array.isArray(value) ? value.filter(object) : [];
const identity = (item: {
  kind?: unknown;
  source_system?: unknown;
  value?: unknown;
}) => JSON.stringify([item.kind, item.source_system, item.value]);

/** Reject old servers that accept the budget but silently compile keyword discovery. */
export function amazonProductScopeEchoesRequest(
  request: MarketMonitorWrite,
  preview: MarketMonitorPreview,
): boolean {
  const scopes = request.source_scopes.filter(
    (scope) => scope.capability === capability || scope.capability === AMAZON_REVIEWS_CAPABILITY,
  );
  if (!scopes.length) return true;
  const normalized = preview.normalized_monitor;
  if (normalized.subject_type !== "product") return false;
  const sites = new Set(scopes.map((scope) => scope.scope.value));
  const identifiers = request.identifiers.filter(
    (item) =>
      ["source_url", "asin"].includes(item.kind) &&
      !amazonStoreTarget(item.value) &&
      (item.source_system === "amazon" || sites.has(item.source_system)),
  );
  const echoedIds = array(normalized.identifiers);
  const expected = new Set<string>();
  for (const id of identifiers) {
    if (
      echoedIds.filter((item) => identity(item) === identity(id)).length !== 1
    )
      return false;
    let url: URL;
    try {
      url = new URL(
        id.kind === "asin"
          ? `https://www.${id.source_system}/dp/${id.value}`
          : id.value,
      );
    } catch {
      return false;
    }
    const site = url.hostname.replace(/^www\./, "");
    if (
      !sites.has(site) ||
      !/^\/dp\/[A-Z0-9]{10}$/.test(url.pathname) ||
      url.href !== `https://www.${site}${url.pathname}`
    )
      return false;
    expected.add(url.href);
  }
  // Internal slices are deliberately not exposed by the authoring API. Verify
  // its public scope/identity/allocation contract; compiler tests own wire proof.
  if (!expected.size) return false;
  if (
    echoedIds.filter(
      (item) =>
        ["source_url", "asin"].includes(String(item.kind)) &&
        !(typeof item.value === 'string' && amazonStoreTarget(item.value)) &&
        (item.source_system === "amazon" ||
          sites.has(String(item.source_system))),
    ).length !== identifiers.length
  )
    return false;
  let logical = 0, allocated = 0;
  for (const scope of scopes) {
    const expectedLimit = scope.max_records_per_slice ?? (scope.capability === capability ? 1 : 0);
    const matches = array(normalized.source_scopes).filter(
      (item) =>
        item.capability === scope.capability &&
        item.source_system === "amazon" &&
        object(item.scope) &&
        item.scope.kind === "marketplace" &&
        item.scope.value === scope.scope.value,
    );
    const count = [...expected].filter((url) =>
      url.startsWith(`https://www.${scope.scope.value}/dp/`),
    ).length;
    const allocation = preview.budget_allocations?.filter(
      (item) =>
        item.capability === scope.capability &&
        item.source_system === "amazon" &&
        item.scope.kind === "marketplace" &&
        item.scope.value === scope.scope.value,
    );
    if (
      matches.length !== 1 ||
      matches[0].max_records_per_slice !== expectedLimit ||
      (scope.capability === capability && matches[0].max_records_per_slice !== 1) ||
      !count ||
      allocation?.length !== 1 ||
      allocation[0].logical_slices !== count ||
      allocation[0].physical_requests !== count ||
      allocation[0].max_records < count ||
      allocation[0].max_records > count * expectedLimit ||
      allocation[0].comment_records !== 0 ||
      preview.source_availability.filter(
        (item) =>
          item.capability === scope.capability &&
          item.source_system === "amazon" &&
          item.scope.kind === "marketplace" &&
          item.scope.value === scope.scope.value,
      ).length !== 1
    )
      return false;
    logical += count;
    allocated += allocation[0].max_records;
  }
  if (scopes.length === request.source_scopes.length &&
      (preview.logical_slices !== logical || preview.physical_requests !== logical || preview.max_records !== allocated
        || array(normalized.source_scopes).length !== scopes.length
        || request.run_budget && allocated > request.run_budget.max_records)) return false;
  return true;
}
